import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Form, Label, Button, Row, Col } from "reactstrap";
import Select from "../common/Select/Select";
import { Input } from "../sites/Input";
import { useReferentiel } from "../datamanagement/useReferentiel";
import { EtudeSiteTable } from "./EtudeSiteTable";
import get from "lodash/get";
import set from "lodash/set";
import mapValues from "lodash/mapValues";
import cloneDeep from "lodash/cloneDeep";
import { DateInput } from "components/sites/DateInput";
import { createEtudeAction } from "actions/etude";

import Notifications from "react-notification-system-redux";

const notifySuccess = (t) =>
  Notifications.success({
    title: t("etudes.creation.notifications.success"),
    position: "bl",
    autoDismiss: 3,
  });

const notifyError = (t) =>
  Notifications.error({
    title: t("etudes.creation.notifications.error"),
    position: "bl",
    autoDismiss: 3,
  });

export const EtudeCreateModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { ListeEnergie, isLoading } = useReferentiel();

  const rawReferentiel = {
    ListeEnergie,
  };

  const formattedOptions = React.useMemo(() => {
    return mapValues(rawReferentiel, (list) => {
      if (!Array.isArray(list)) return [];

      return list.map((item) => ({
        value: item.Id,
        label: item.Nom,
      }));
    });
  }, [ListeEnergie]);

  const { ListeEnergie: energyOptions } = formattedOptions;

  const [formData, setFormData] = useState({
    name: "",
    energy: null,
    initialState: null,
    finalState: null,
    periodType: null,
    startDate: "",
    endDate: "",
  });

  const [selectedPeeIds, setSelectedPeeIds] = useState([]);

  const handleChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const onChange = React.useCallback(
    (path, value) => {
      const newData = cloneDeep(formData);
      set(newData, path, value);
      setFormData(newData);
    },
    [formData],
  );

  const stateOptions = [
    { value: 1, label: t("etudes.subscriptionType.1") },
    { value: 2, label: t("etudes.subscriptionType.2") },
    { value: 3, label: t("etudes.subscriptionType.3") },
  ];
  const periodOptions = [
    { value: 1, label: t("etudes.periodType.1") },
    { value: 2, label: t("etudes.periodType.2") },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      Name: formData.name,
      Commodity: formData.energy?.value,
      InitialSubscription: formData.initialState?.value,
      TargetSubscription: formData.finalState?.value,
      PeriodType: formData.periodType?.value,
      PeeIds: selectedPeeIds,
      Status: 1, // Statut "Initialisé"
    };

    if (formData.startDate) {
      payload.DateDebut = formData.startDate;
    }

    if (formData.endDate) {
      payload.DateFin = formData.endDate;
    }

    try {
      await dispatch(createEtudeAction(payload));
      dispatch(notifySuccess(t));
    } catch (error) {
      console.error("Create failed:", error);
      dispatch(notifyError(t));
    }
  };

  return (
    <div
      className="m-3"
      css={{
        button: {
          i: { position: "relative", top: -1, marginRight: 8 },
        },
        h5: { fontWeight: "bold", color: "#61747f", marginBottom: "1.5rem" },
        ".h7": {
          fontWeight: "bold",
          color: "#8c99a5",
          textTransform: "uppercase",
          marginBottom: "1rem",
        },
        "label:not(.toggle-label)": {
          display: "flex",
          alignItems: "center",
          marginBottom: ".5rem",
          ">div:first-of-type": {
            width: 120,
            fontWeight: "bold",
            paddingRight: "0.6rem",
          },
          ".form-control, .react-select": {
            flex: 1,
            maxWidth: 250,
            "&:disabled": { backgroundColor: "#fff" },
          },
        },
        "th, td, .form-control, .react-select": { fontSize: 13 },
      }}
    >
      <section className="mb-4">
        <Form onSubmit={handleSubmit}>
          <div className="infos">
            <div className="h7">{t("etudes.creation.sectionTitle")}</div>

            <Row>
              <Col md={6}>
                <Label>
                  <div>{t("etudes.creation.name")}</div>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(value) => handleChange("name", value)}
                    required
                  />
                </Label>
              </Col>
              <Col md={6}>
                <Label>
                  <div>{t("etudes.creation.energy") || "Energie"}</div>
                  <Select
                    placeholder={""}
                    value={formData.energy}
                    onChange={(selected) => handleChange("energy", selected)}
                    options={energyOptions}
                    isSearchable={true}
                  />
                </Label>
              </Col>
            </Row>

            <Row className="mt-2">
              <Col md={6}>
                <Label>
                  <div>{t("etudes.creation.initialStatus")}</div>
                  <Select
                    placeholder={""}
                    value={formData.initialState}
                    onChange={(selected) =>
                      handleChange("initialState", selected)
                    }
                    options={stateOptions}
                    isSearchable={true}
                  />
                </Label>
              </Col>
              <Col md={6}>
                <Label>
                  <div>{t("etudes.creation.finalStatus")}</div>
                  <Select
                    placeholder={""}
                    value={formData.finalState}
                    onChange={(selected) =>
                      handleChange("finalState", selected)
                    }
                    options={stateOptions}
                    isSearchable={true}
                  />
                </Label>
              </Col>
            </Row>

            <Row className="mt-2">
              <Col md={6}>
                <Label>
                  <div>{t("etudes.creation.period")}</div>
                  <Select
                    width={600}
                    placeholder={""}
                    value={formData.periodType}
                    onChange={(selected) =>
                      handleChange("periodType", selected)
                    }
                    options={periodOptions}
                    isSearchable={true}
                  />
                </Label>
              </Col>
            </Row>

            {formData.periodType?.value === 2 && (
              <Row
                className="mt-3 ml-1 pl-3 border-left"
                style={{ borderColor: "#cbd5e1" }}
              >
                <Col md={6}>
                  <Label>
                    <div>{t("etudes.creation.startDate")}</div>
                    <DateInput
                      value={get(formData, "startDate", "")}
                      onChange={(value) => handleChange("startDate", value)}
                    />
                  </Label>
                </Col>
                <Col md={6}>
                  <Label>
                    <div>{t("etudes.creation.endDate")}</div>
                    <DateInput
                      value={get(formData, "endDate", "")}
                      onChange={(value) => handleChange("endDate", value)}
                    />
                  </Label>
                </Col>
              </Row>
            )}
          </div>

          <section className="mt-4">
            <div className="infos">
              <div className="h7">{t("etudes.creation.table.title")}</div>
              <EtudeSiteTable
                energyType={formData.energy?.value}
                selectedIds={selectedPeeIds}
                onChangeSelection={setSelectedPeeIds}
              />
            </div>
          </section>

          <footer className="mt-3 d-flex justify-content-end">
            <Button
              type="submit"
              color="primary"
              size="sm"
              css={{ padding: "6px 12px" }}
              onClick={() => {
                handleSubmit;
              }}
            >
              <i className="icon-done" />
              <span>{t("etudes.creation.save")}</span>
            </Button>
          </footer>
        </Form>
      </section>
    </div>
  );
};
